import { BaseResponse } from "@app/entities/BaseResponse";
import { commonHttpApi } from "./commonHttp.api";
import { ApiError } from "./ApiError";
import i18n from '@app/i18n';
import { IList } from "@app/entities/IListResponse";
import { apiRequest } from './apiUtils';

export interface Report {
    name: string;
    systemName: string;
    isExternalLink: boolean;
    externalUrl: string;
}

export interface ReportFilter {
    name: string;
    fieldName: string;
    defaultValue: string;
    placeholder: string;
    autoMappingType: string;
    inputType: string;
    listItem: string;
}

export interface ReportColumn {
    displayName: string;
    fieldName: string;
    displayType: string;
    route: string;
}

export interface ReportProps {
    id: number;
    name: string;
    reportColumns: ReportColumn[];
    systemName: string;
    reportFilters: ReportFilter[];
}

export interface ReportProps {
    id: number;
    name: string;
    reportColumns: ReportColumn[];
    systemName: string;
    reportFilters: ReportFilter[];
}

export interface IUpdateReportUserRoleModel
{
  systemName: string,
  reportId: number
}

export const getReports = async(categoryId: number) : Promise<BaseResponse<Report[]> | undefined> => {
    let result: BaseResponse<Report[]> | undefined = undefined;
    await commonHttpApi
        .get<BaseResponse<Report[]>>(`report-viewer/${categoryId}`)
        .then(({ data }) => {
            result = data;
        })
        .catch((err) => {
            if (err.options) {
                if (err.options.status === 401) {
                    switch (err.options.code) {
                        case '1000002':
                        case '1000013':
                        case '999999':
                            throw new ApiError(i18n.t('login.loginError'));
                    }
                }
            }

            throw new ApiError(i18n.t('serverError.main'));
        });

    return result;
}

export const cloneReport = async (reportId: number): Promise<BaseResponse<boolean>> => {
    let result: BaseResponse<boolean> = {} as BaseResponse<boolean>;

    await commonHttpApi
        .post<BaseResponse<boolean>>(`report-categories/${reportId}/clone`)
        .then(({ data }) => {
            result = data;
        })
        .catch((err) => {
            if (err.response) {
                if (err.response.status === 401) {
                    switch (err.response.data?.code) {
                        case '1000002':
                        case '1000013':
                        case '999999':
                            throw new ApiError(i18n.t('login.loginError'));
                    }
                }
            }

            throw new ApiError(i18n.t('serverError.main'));
        });

    return result;
};
export const getReportProp = async(categoryId: number, systemName: string): Promise<BaseResponse<ReportProps>> => {
    let result: BaseResponse<ReportProps> = {} as BaseResponse<ReportProps>;
    await commonHttpApi
        .get<BaseResponse<ReportProps>>(`report-viewer/${categoryId}/${systemName}`)
        .then(({ data }) => {
            result = data;
        })
        .catch((err) => {
            if (err.options) {
                if (err.options.status === 401) {
                    switch (err.options.code) {
                        case '1000002':
                        case '1000013':
                        case '999999':
                            throw new ApiError(i18n.t('login.loginError'));
                    }
                }
            }

            throw new ApiError(i18n.t('serverError.main'));
        });
        
    return result;
}

export const getSubReportProp = async(systemName: string): Promise<BaseResponse<ReportProps>> => {
    let result: BaseResponse<ReportProps> = {} as BaseResponse<ReportProps>;
    await commonHttpApi
        .get<BaseResponse<ReportProps>>(`report-viewer/sub-report/info/${systemName}`)
        .then(({ data }) => {
            result = data;
        })
        .catch((err) => {
            if (err.options) {
                if (err.options.status === 401) {
                    switch (err.options.code) {
                        case '1000002':
                        case '1000013':
                        case '999999':
                            throw new ApiError(i18n.t('login.loginError'));
                    }
                }
            }

            throw new ApiError(i18n.t('serverError.main'));
        });
        
    return result;
}

export const getReportResult = async(categoryId: number, systemName: string, paramaters: any, pageSize: number, current: number): Promise<BaseResponse<IList<any>>> => {
    let result: BaseResponse<IList<any>> = {} as BaseResponse<IList<any>>;
    const queryString = new URLSearchParams(paramaters).toString();
    let apiUrl = `report-viewer/${categoryId}/${systemName}/search/?pageSize=${pageSize}&page=${current}`;
    if (queryString) {
        apiUrl += `&${queryString}`;
    }

    await commonHttpApi
    .get<BaseResponse<IList<any>>>(apiUrl)
    .then(({ data }) => {
        result = data;
    })
    .catch((err) => {
        if (err.options) {
            if (err.options.status === 401) {
                switch (err.options.code) {
                    case '1000002':
                    case '1000013':
                    case '999999':
                        throw new ApiError(i18n.t('login.loginError'));
                }
            }
        }

        throw new ApiError(i18n.t('serverError.main'));
    });
    return result;
}

export const getSubReportResult = async(route: string, paramaters: any, pageSize: number, current: number): Promise<BaseResponse<IList<any>>> => {
    let result: BaseResponse<IList<any>> = {} as BaseResponse<IList<any>>;
    const queryString = new URLSearchParams(paramaters).toString();
    let apiUrl = `report-viewer/sub-report/search/${route}/?pageSize=${pageSize}&page=${current}`;
    if (queryString) {
        apiUrl += `&${queryString}`;
    }

    await commonHttpApi
    .get<BaseResponse<IList<any>>>(apiUrl)
    .then(({ data }) => {
        result = data;
    })
    .catch((err) => {
        if (err.options) {
            if (err.options.status === 401) {
                switch (err.options.code) {
                    case '1000002':
                    case '1000013':
                    case '999999':
                        throw new ApiError(i18n.t('login.loginError'));
                }
            }
        }

        throw new ApiError(i18n.t('serverError.main'));
    });
    return result;
}

export const getExcelReport = async (categoryId: number, systemName: string, paramaters: any): Promise<any> => {
    let result: BaseResponse<IList<any>> = {} as BaseResponse<IList<any>>;
    const queryString = new URLSearchParams(paramaters).toString();
    let apiUrl = `report-viewer/${categoryId}/${systemName}/download-excel/`;
    if (queryString) {
        apiUrl += `?${queryString}`;
    }

    await commonHttpApi
    .get<BaseResponse<IList<any>>>(apiUrl)
    .then(({ data }) => {
        result = data;
    })
    .catch((err) => {
        if (err.options) {
            if (err.options.status === 401) {
                switch (err.options.code) {
                    case '1000002':
                    case '1000013':
                    case '999999':
                        throw new ApiError(i18n.t('login.loginError'));
                }
            }
        }

        throw new ApiError(i18n.t('serverError.main'));
    });
    return result;
}

export const getExcelSubReport = async(route: string, paramaters: any): Promise<any> => {
    let result: BaseResponse<IList<any>> = {} as BaseResponse<IList<any>>;
    const queryString = new URLSearchParams(paramaters).toString();
    let apiUrl = `report-viewer/sub-report/download-excel/${route}`;
    if (queryString) {
        apiUrl += `?${queryString}`;
    }

    await commonHttpApi
    .get<BaseResponse<IList<any>>>(apiUrl)
    .then(({ data }) => {
        result = data;
    })
    .catch((err) => {
        if (err.options) {
            if (err.options.status === 401) {
                switch (err.options.code) {
                    case '1000002':
                    case '1000013':
                    case '999999':
                        throw new ApiError(i18n.t('login.loginError'));
                }
            }
        }

        throw new ApiError(i18n.t('serverError.main'));
    });
    return result;
}

export const getAllReportAccess = async () : Promise<BaseResponse<IList<any>> | undefined> => {
  let result: BaseResponse<IList<any>> | undefined = undefined;
  await commonHttpApi
    .get<BaseResponse<IList<any>>>("report-categories/getAllReportAccess")
    .then(({ data }) => {
      result = data;
    })
    .catch((err) => {
      if (err.options) {
        if (err.options.status === 401) {
          switch (err.options.code) {
            case "1000002":
            case "1000013":
              throw new ApiError(i18n.t("login.loginError"));
          }
        }
      }

      throw new ApiError(i18n.t("serverError.main"));
    }); 

  return result;
}

export const testFunction = async () : Promise<any> => {
  let result: BaseResponse<IList<any>> | undefined = undefined;
  await commonHttpApi
    .get<BaseResponse<IList<any>>>("report-categories/testFunction")
    .then(({ data }) => {
      result = data;
    })
    .catch((err) => {
      if (err.options) {
        if (err.options.status === 401) {
          switch (err.options.code) {
            case "1000002":
            case "1000013":
              throw new ApiError(i18n.t("login.loginError"));
          }
        }
      }

      throw new ApiError(i18n.t("serverError.main"));
    }); 

  return result;
}


export const getRolesReportAccess = async (
  userRoleId: string
): Promise<BaseResponse<IList<any>> | undefined> => {
  let result: BaseResponse<IList<any>> | undefined = undefined;
  await commonHttpApi
    .get<BaseResponse<IList<any>>>(
      "report-categories/" + userRoleId + "/getRoleReportAccess"
    )
    .then(({ data }) => {
      result = data;
    })
    .catch((err) => {
      if (err.options) {
        if (err.options.status === 401) {
          switch (err.options.code) {
            case "1000002":
            case "1000013":
              throw new ApiError(i18n.t("login.loginError"));
          }
        }
      }

      throw new ApiError(i18n.t("serverError.main"));
    });

  return result;
};


export const saveUserRoleReportAccess = async (
  userRoleId: string,
  permissionsPayload: IUpdateReportUserRoleModel[]
): Promise<BaseResponse<any> | undefined> => {
  let result: BaseResponse<any> | undefined = undefined;

  await commonHttpApi
    .post<BaseResponse<any>>("report-categories/" + userRoleId + "/saveUserRoleReportAccess", permissionsPayload)
    .then(({ data }) => {
      result = data;
    })
    .catch((err) => {
      if (err.options) {
        if (err.options.status === 401) {
          switch (err.options.code) {
            case "1000002":
            case "1000013":
              throw new ApiError(i18n.t("login.loginError"));
          }
        }
      }

      throw new ApiError(i18n.t("serverError.main"));
    });

  return result;
}

export const GetActiveReportCategoriesByUserRoleId = async (
): Promise<any> => {
  return await apiRequest<any>(
    "GET",
    `report-categories/reports/GetActiveReportCategoriesByUserRoleId`
  );
};
